import { Flex } from 'components/Flex';
import {
  GreyText,
  InnerHeaderWrapper,
  MainTitle,
  NavigationButton,
  NavigationButtonsWrapper,
  AgencyAccept,
  ShiftStatusText,
  ShiftStatusTextWrapper,
  Text,
  ShiftStatusDot,
  CommunityName,
} from 'components/ShiftModal/src/views/DetailView/DetailView.styled';
import { FC, Fragment, useState } from 'react';
import { css } from 'styled-components';
import { useTranslation } from 'hooks/useTranslation';
import { PATH_DETAIL_VIEW_TAB_MAP } from '../DetailView.constants';
import { UserGuard } from 'guards/UserGuard';
import { ShiftModalOrderDetailsQuery } from 'types/graphql';
import {
  getShiftModalShiftDate,
  getShiftModalShiftTimeRange,
  getShiftModalShiftTotalDuration,
} from 'mappers/shiftModalOrderDetailsMapper';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import { usePathname } from 'hooks/usePathname';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import { ActivitiesType } from 'types/users.types';
import {
  DetailViewActiveTabType,
  DetailViewComponentMap,
  IDetailViewComponentProps,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/OpenShifts/DetailView/DetailView.types';
import { useRemovePositionModalUIContext } from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { DeleteOpenShiftDialog } from 'pages/LocationSettingsPage/PositionsAllocation/DeleteOpenShiftDialog';
import { BKJButton } from '@awais4123/fillify-design-library';
import { ShiftCardIcon } from 'components/ShiftCardSkeleton/ShiftCardSkeleton.styled';

export function DetailViewInnerHeader({ data }: { data: ShiftModalOrderDetailsQuery }) {
  const { t, i18n } = useTranslation();
  const isAcceptedByAgency = !!data.fetchOrder.orderSchedule.mainOrderJane?.assignedBy?.displayId;

  return (
    <InnerHeaderWrapper>
      <MainTitle>{data.fetchOrder!.position.name}</MainTitle>
      <UserGuard userType="USER_TYPE_AGENCY">
        <CommunityName>{data.fetchOrder!.community.name}</CommunityName>
      </UserGuard>
      <ShiftStatusTextWrapper>
        <ShiftStatusDot type={data.fetchOrder!.status.name.toLowerCase()}>•</ShiftStatusDot>
        <ShiftStatusText>
          {getTranslatedResponse(data.fetchOrder?.status.name, 'status', i18n, t)}
          {!!isAcceptedByAgency && <AgencyAccept>&nbsp;(by Agency Admin)</AgencyAccept>}
        </ShiftStatusText>
      </ShiftStatusTextWrapper>
      <Flex>
        <Text>{getShiftModalShiftDate(data)}</Text>
      </Flex>
      <Flex>
        {data.fetchOrder?.recurrence !== null && (
          <ShiftCardIcon iconName="Recurring" fontSize={24} />
        )}
        <Text>{getShiftModalShiftTimeRange(data)}</Text>
        &nbsp;
        <GreyText>({getShiftModalShiftTotalDuration(data)})</GreyText>
      </Flex>
    </InnerHeaderWrapper>
  );
}

const tabs: [string, DetailViewActiveTabType][] = [
  ['General', 'GeneralTab'],
  ['Activity', 'ActivityTab'],
];

export const DetailTabView: FC<IDetailViewComponentProps> = ({ data }) => {
  const [isDeleteOpenShiftConfirmationOpen, setIsDeleteOpenShiftConfirmationOpen] = useState(false);
  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState<DetailViewActiveTabType>(
    PATH_DETAIL_VIEW_TAB_MAP(pathname),
  );
  const { shift_id } = useRemovePositionModalUIContext();
  const { t, i18n } = useTranslation();
  const ActiveTabComponent = DetailViewComponentMap[activeTab];

  const { timeZone } = getCurrentlySelectedLocation();
  return (
    <Fragment>
      {isDeleteOpenShiftConfirmationOpen && (
        <DeleteOpenShiftDialog
          shiftId={String(shift_id)}
          isDeleteOpenShiftConfirmationOpen={isDeleteOpenShiftConfirmationOpen}
          setIsDeleteOpenShiftConfirmationOpen={setIsDeleteOpenShiftConfirmationOpen}
        />
      )}
      <Flex
        flexDirection="column"
        height="fit-content"
        minHeight="fit-content"
        css={css`
          position: sticky;
          background-color: white;
          z-index: 1;
          top: 0;
        `}
      >
        <NavigationButtonsWrapper>
          {tabs.map(([label, _enum]) => {
            return (
              <NavigationButton
                key={label}
                isDisabled={!!data.fetchOrder!.isAway && label === 'Activity'}
                onClick={(e) => {
                  e.preventDefault();
                  if (activeTab !== _enum) {
                    setActiveTab(_enum);
                  }
                }}
                isActive={activeTab === _enum}
              >
                {getTranslatedResponse(label, 'shift_modal', i18n, t)}
              </NavigationButton>
            );
          })}
        </NavigationButtonsWrapper>
      </Flex>

      <ActiveTabComponent
        timeZone={timeZone}
        activities={data?.fetchOrder?.activities as ActivitiesType[]}
        data={data}
      />

      {activeTab === 'GeneralTab' && (
        <BKJSideModalFooter>
          <BKJButton
            variant="RedText"
            onClick={() => setIsDeleteOpenShiftConfirmationOpen(true)}
            type="button"
            css={css`
              text-decoration: none;
            `}
          >
            {t('shift_modal:CANCEL')}
          </BKJButton>
        </BKJSideModalFooter>
      )}
    </Fragment>
  );
};
